/**
 * Job Service Purchase Component
 * Handles purchasing service bundles, subscriptions, and single services
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '../ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import {
  Package,
  Repeat,
  FileText,
  CheckCircle2,
  Sparkles,
  ChevronRight,
  Loader2,
  CreditCard,
  Gift,
  Star,
  Crown,
  Zap,
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { useCountry } from '../../context/CountryContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Currency config
const CURRENCY_CONFIG = {
  UK: { code: 'GBP', symbol: '£' },
  SL: { code: 'LKR', symbol: 'Rs' },
};


// Bundle Card Component
const BundleCard = ({ bundle, onPurchase, loading }) => {
  const badgeColors = {
    green: 'bg-green-500',
    blue: 'bg-blue-500',
    purple: 'bg-purple-500',
    amber: 'bg-amber-500',
    red: 'bg-red-500',
  };
  
  return (
    <Card 
      className="relative overflow-hidden border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50 hover:border-blue-400 transition-all"
      data-testid={`bundle-card-${bundle.id}`}
    >
      {/* Badges Container */}
      <div className="absolute top-0 right-0 flex flex-col items-end">
        {bundle.badge && (
          <div className={`${badgeColors[bundle.badge_color] || 'bg-green-500'} text-white text-xs font-bold px-3 py-1 rounded-bl-lg flex items-center gap-1`}>
            <Star className="h-3 w-3" />
            {bundle.badge}
          </div>
        )}
        {bundle.savings_text && (
          <div className="bg-emerald-500 text-white text-xs font-bold px-2 py-0.5 mt-0.5 rounded-l-lg">
            {bundle.savings_text}
          </div>
        )}
      </div>
      <CardContent className="p-5 pt-8">
        <div className="flex items-start gap-3 mb-4">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Package className="h-6 w-6 text-blue-600" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-gray-900 text-lg">{bundle.name}</h3>
            <p className="text-sm text-gray-500 mt-1">Complete package for job seekers</p>
          </div>
        </div>
        
        <div className="space-y-2 mb-4">
          <p className="text-xs font-medium text-gray-500 uppercase">Includes:</p>
          {bundle.includes?.map((service, idx) => (
            <div key={idx} className="flex items-center gap-2 text-sm text-gray-700">
              <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0" />
              <span>{service.icon} {service.name}</span>
            </div>
          ))}
        </div>
        
        <div className="flex items-center justify-between pt-3 border-t">
          <div>
            <span className="text-2xl font-bold text-gray-900">
              {bundle.currency_symbol}{bundle.price}
            </span>
            <span className="text-sm text-gray-500 ml-1">one-time</span>
          </div>
          <Button 
            onClick={() => onPurchase(bundle)}
            disabled={loading}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid={`buy-bundle-${bundle.id}`}
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CreditCard className="h-4 w-4 mr-2" />}
            Buy Now
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};


// Subscription Card Component
const SubscriptionCard = ({ subscription, onPurchase, loading }) => {
  return (
    <Card 
      className="relative overflow-hidden border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-pink-50 hover:border-purple-400 transition-all"
      data-testid={`subscription-card-${subscription.id}`}
    >
      <div className="absolute top-0 right-0 bg-purple-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg flex items-center gap-1">
        <Crown className="h-3 w-3" />
        Monthly
      </div>
      <CardContent className="p-5">
        <div className="flex items-start gap-3 mb-4">
          <div className="p-2 bg-purple-100 rounded-lg">
            <Repeat className="h-6 w-6 text-purple-600" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-gray-900 text-lg">{subscription.name}</h3>
            <p className="text-sm text-gray-500 mt-1">Ongoing career support</p>
          </div>
        </div>
        
        <div className="space-y-2 mb-4">
          <p className="text-xs font-medium text-gray-500 uppercase">Features:</p>
          {subscription.features?.map((feature, idx) => (
            <div key={idx} className="flex items-center gap-2 text-sm text-gray-700">
              <Star className="h-4 w-4 text-purple-500 flex-shrink-0" />
              <span>{feature}</span>
            </div>
          ))}
        </div>
        
        <div className="flex items-center justify-between pt-3 border-t">
          <div>
            <span className="text-2xl font-bold text-gray-900">
              {subscription.currency_symbol}{subscription.price}
            </span>
            <span className="text-sm text-gray-500 ml-1">/ {subscription.billing_period}</span>
          </div>
          <Button 
            onClick={() => onPurchase(subscription)}
            disabled={loading}
            className="bg-purple-600 hover:bg-purple-700"
            data-testid={`subscribe-${subscription.id}`}
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Zap className="h-4 w-4 mr-2" />}
            Subscribe
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};


// Checkout Dialog Component
const CheckoutDialog = ({ open, onClose, item, itemType, onConfirm, loading }) => {
  const [form, setForm] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
  });
  
  const handleSubmit = (e) => {
    e.preventDefault();
    onConfirm(form);
  };
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-blue-600" />
            Complete Your Purchase
          </DialogTitle>
          <DialogDescription>
            {itemType === 'bundle' ? 'Service Bundle' : 'Subscription'}: {item?.name}
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Total:</span>
              <span className="text-lg font-bold">
                {item?.currency_symbol}{item?.price}
                {item?.billing_period && <span className="text-sm font-normal">/{item.billing_period}</span>}
              </span>
            </div>
          </div>
          
          <div>
            <Label htmlFor="name">Full Name *</Label>
            <Input
              id="name"
              value={form.customer_name}
              onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
              placeholder="Your name"
              required
            />
          </div>
          
          <div>
            <Label htmlFor="email">Email *</Label>
            <Input
              id="email"
              type="email"
              value={form.customer_email}
              onChange={(e) => setForm({ ...form, customer_email: e.target.value })}
              placeholder="your@email.com"
              required
            />
          </div>
          
          <div>
            <Label htmlFor="phone">Phone</Label>
            <Input
              id="phone"
              value={form.customer_phone}
              onChange={(e) => setForm({ ...form, customer_phone: e.target.value })}
              placeholder="+44 7XX XXX XXXX"
            />
          </div>
          
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading} className="bg-green-600 hover:bg-green-700">
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Processing...
                </>
              ) : (
                <>
                  <CreditCard className="h-4 w-4 mr-2" />
                  Proceed to Payment
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};


// Main Component
const JobServicePurchase = ({ jobCategory, onClose }) => {
  const { country } = useCountry();
  const { toast } = useToast();
  const [bundles, setBundles] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [categoryName, setCategoryName] = useState('');
  const [loading, setLoading] = useState(true);
  const [purchaseLoading, setPurchaseLoading] = useState(false);
  const [checkoutItem, setCheckoutItem] = useState(null);
  const [checkoutType, setCheckoutType] = useState(null);
  const [showCheckoutDialog, setShowCheckoutDialog] = useState(false);
  
  const currency = CURRENCY_CONFIG[country] || CURRENCY_CONFIG.UK;
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch bundles and subscriptions in parallel
        const [bundlesRes, subscriptionsRes] = await Promise.all([
          axios.get(`${API_URL}/api/job-services/bundles/${jobCategory}`, { params: { country } }),
          axios.get(`${API_URL}/api/job-services/subscriptions/${jobCategory}`, { params: { country } }),
        ]);
        
        if (bundlesRes.data.success) {
          setBundles(bundlesRes.data.bundles || []);
          setCategoryName(bundlesRes.data.category_name || jobCategory);
        }
        
        if (subscriptionsRes.data.success) {
          setSubscriptions(subscriptionsRes.data.subscriptions || []);
        }
      } catch (error) {
        console.error('Error fetching service packages:', error);
        toast({
          title: 'Error',
          description: 'Failed to load service packages',
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    };
    
    if (jobCategory) {
      fetchData();
    }
  }, [jobCategory, country, toast]);
  
  const handlePurchaseBundle = (bundle) => {
    setCheckoutItem(bundle);
    setCheckoutType('bundle');
    setShowCheckoutDialog(true);
  };
  
  const handlePurchaseSubscription = (subscription) => {
    setCheckoutItem(subscription);
    setCheckoutType('subscription');
    setShowCheckoutDialog(true);
  };
  
  const handleConfirmPurchase = async (customerInfo) => {
    try {
      setPurchaseLoading(true);
      
      const endpoint = checkoutType === 'bundle' 
        ? `${API_URL}/api/job-services/purchase/bundle/checkout`
        : `${API_URL}/api/job-services/purchase/subscription/checkout`;
      
      const payload = {
        job_category: jobCategory,
        [checkoutType === 'bundle' ? 'bundle_id' : 'subscription_id']: checkoutItem.id,
        origin_url: window.location.origin,
        ...customerInfo,
      };
      
      const response = await axios.post(endpoint, payload);
      
      if (response.data.success && response.data.checkout_url) {
        toast({
          title: 'Redirecting to payment...',
          description: 'You will be redirected to our secure payment page',
        });
        
        // Redirect to Stripe checkout
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error('Failed to create checkout session');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast({
        title: 'Payment Error',
        description: error.response?.data?.detail || 'Failed to initiate payment',
        variant: 'destructive',
      });
    } finally {
      setPurchaseLoading(false);
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }
  
  const hasBundles = bundles.length > 0;
  const hasSubscriptions = subscriptions.length > 0;
  
  if (!hasBundles && !hasSubscriptions) {
    return (
      <Card className="p-6 text-center">
        <Package className="h-12 w-12 mx-auto mb-4 text-gray-300" />
        <p className="text-gray-500">No service packages available for this category</p>
      </Card>
    );
  }
  
  return (
    <div className="space-y-6" data-testid="job-service-purchase">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-blue-600" />
            {categoryName} Career Services
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Choose a package to boost your job search
          </p>
        </div>
        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
          <Gift className="h-3 w-3 mr-1" />
          Special Offers
        </Badge>
      </div>
      
      <Tabs defaultValue={hasBundles ? 'bundles' : 'subscriptions'} className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="bundles" disabled={!hasBundles}>
            <Package className="h-4 w-4 mr-2" />
            Bundles ({bundles.length})
          </TabsTrigger>
          <TabsTrigger value="subscriptions" disabled={!hasSubscriptions}>
            <Repeat className="h-4 w-4 mr-2" />
            Subscriptions ({subscriptions.length})
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="bundles" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {bundles.map((bundle) => (
              <BundleCard
                key={bundle.id}
                bundle={bundle}
                onPurchase={handlePurchaseBundle}
                loading={purchaseLoading}
              />
            ))}
          </div>
        </TabsContent>
        
        <TabsContent value="subscriptions" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {subscriptions.map((subscription) => (
              <SubscriptionCard
                key={subscription.id}
                subscription={subscription}
                onPurchase={handlePurchaseSubscription}
                loading={purchaseLoading}
              />
            ))}
          </div>
        </TabsContent>
      </Tabs>
      
      {/* Checkout Dialog */}
      <CheckoutDialog
        open={showCheckoutDialog}
        onClose={() => setShowCheckoutDialog(false)}
        item={checkoutItem}
        itemType={checkoutType}
        onConfirm={handleConfirmPurchase}
        loading={purchaseLoading}
      />
    </div>
  );
};


// Mobile Sheet Trigger for Service Purchase
export const JobServicePurchaseSheet = ({ jobCategory, children }) => {
  const [open, setOpen] = useState(false);
  
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        {children || (
          <Button variant="outline" className="w-full" data-testid="open-service-purchase-sheet">
            <Package className="h-4 w-4 mr-2" />
            View Service Packages
          </Button>
        )}
      </SheetTrigger>
      <SheetContent side="bottom" className="h-[85vh] rounded-t-2xl">
        <SheetHeader className="mb-4">
          <SheetTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-blue-600" />
            Career Service Packages
          </SheetTitle>
        </SheetHeader>
        <div className="overflow-y-auto h-[calc(100%-80px)]">
          <JobServicePurchase jobCategory={jobCategory} onClose={() => setOpen(false)} />
        </div>
      </SheetContent>
    </Sheet>
  );
};


// Success Page Component
export const JobServicePurchaseSuccess = () => {
  const [status, setStatus] = useState('loading');
  const [purchaseDetails, setPurchaseDetails] = useState(null);
  const { toast } = useToast();
  
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    const purchaseType = params.get('type');
    
    if (!sessionId) {
      setStatus('error');
      return;
    }
    
    const checkStatus = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/job-services/purchase/status/${sessionId}`);
        
        if (response.data.success) {
          if (response.data.payment_status === 'paid') {
            setStatus('success');
            setPurchaseDetails(response.data);
            toast({
              title: 'Payment Successful!',
              description: 'Your service package has been activated',
            });
          } else if (response.data.payment_status === 'unpaid') {
            setStatus('pending');
          } else {
            setStatus('error');
          }
        }
      } catch (error) {
        console.error('Error checking payment status:', error);
        setStatus('error');
      }
    };
    
    checkStatus();
  }, [toast]);
  
  if (status === 'loading') {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <Loader2 className="h-12 w-12 animate-spin text-blue-600 mb-4" />
        <p className="text-gray-600">Confirming your payment...</p>
      </div>
    );
  }
  
  if (status === 'success') {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] p-8">
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-6">
          <CheckCircle2 className="h-12 w-12 text-green-600" />
        </div>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Payment Successful!</h1>
        <p className="text-gray-600 text-center mb-6">
          Your career service package has been activated.
          {purchaseDetails?.transaction_id && (
            <span className="block mt-2 text-sm">
              Reference: <strong>{purchaseDetails.transaction_id}</strong>
            </span>
          )}
        </p>
        <div className="flex gap-4">
          <Button onClick={() => window.location.href = '/jobs'}>
            Browse Jobs
          </Button>
          <Button variant="outline" onClick={() => window.location.href = '/profile'}>
            View My Services
          </Button>
        </div>
      </div>
    );
  }
  
  if (status === 'pending') {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] p-8">
        <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mb-6">
          <Loader2 className="h-12 w-12 text-yellow-600 animate-spin" />
        </div>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Payment Processing</h1>
        <p className="text-gray-600 text-center mb-6">
          Your payment is being processed. This may take a few moments.
        </p>
        <Button onClick={() => window.location.reload()}>
          Check Status
        </Button>
      </div>
    );
  }
  
  return (
    <div className="flex flex-col items-center justify-center min-h-[400px] p-8">
      <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mb-6">
        <FileText className="h-12 w-12 text-red-600" />
      </div>
      <h1 className="text-2xl font-bold text-gray-900 mb-2">Something Went Wrong</h1>
      <p className="text-gray-600 text-center mb-6">
        We couldn't confirm your payment. Please contact support if you were charged.
      </p>
      <Button onClick={() => window.location.href = '/jobs'}>
        Return to Jobs
      </Button>
    </div>
  );
};


export default JobServicePurchase;
